import { useRef } from "react";
import { motion, useReducedMotion, useScroll, useSpring } from "framer-motion";
import Reveal from "@/components/Reveal";
import PhoneMockup from "@/components/PhoneMockup";
import { STEPS, ASSETS } from "@/config/site";

export const HowItWorks = () => {
  const reduce = useReducedMotion();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start 70%", "end 60%"] });
  const pathLength = useSpring(scrollYProgress, { stiffness: 90, damping: 20 });

  return (
    <section id="how-it-works" ref={ref} className="relative scroll-mt-24 overflow-hidden py-24 sm:py-32">
      <div className="container-nova">
        <Reveal>
          <p className="label-eyebrow mb-4">How Nova works</p>
          <h2 className="max-w-2xl font-heading text-3xl font-800 leading-tight tracking-tight text-white sm:text-5xl" style={{ fontWeight: 800 }}>
            Three steps to a <span className="text-gradient-accent">clearer plan</span>
          </h2>
        </Reveal>

        <div className="mt-16 grid gap-12 lg:grid-cols-[1fr_0.9fr] lg:items-center">
          {/* Steps with animated progress path */}
          <div className="relative pl-12">
            <svg className="absolute left-4 top-2 h-[calc(100%-1rem)] w-6" viewBox="0 0 6 100" preserveAspectRatio="none" aria-hidden="true">
              <line x1="3" y1="0" x2="3" y2="100" stroke="rgba(255,255,255,0.08)" strokeWidth="1.5" />
              <motion.line
                x1="3" y1="0" x2="3" y2="100"
                stroke="url(#nova-grad)" strokeWidth="2"
                style={{ pathLength: reduce ? 1 : pathLength }}
              />
              <defs>
                <linearGradient id="nova-grad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#00F0FF" />
                  <stop offset="100%" stopColor="#00FF9D" />
                </linearGradient>
              </defs>
            </svg>

            <div className="space-y-10">
              {STEPS.map((s, i) => (
                <Reveal key={s.n} delay={i * 0.08}>
                  <div className="relative" data-testid={`step-${i + 1}`}>
                    <div className="absolute -left-[3.25rem] top-0 flex h-9 w-9 items-center justify-center rounded-full border border-white/15 bg-nova-surface font-heading text-sm font-bold text-nova-cyan">
                      {i + 1}
                    </div>
                    <span className="label-eyebrow text-white/40">{s.n}</span>
                    <h3 className="mt-2 font-heading text-xl font-bold text-white sm:text-2xl">{s.title}</h3>
                    <p className="mt-2 max-w-md text-white/60">{s.desc}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>

          <Reveal delay={0.1}>
            <div className="relative mx-auto w-full max-w-[300px]">
              <div className="aurora-blob left-1/2 top-1/2 h-64 w-64 -translate-x-1/2 -translate-y-1/2 bg-nova-emerald/15" />
              <PhoneMockup src={ASSETS.screens.s1} alt="Nova app active dashboard showing goals and debt-free forecast" float priority />
            </div>
          </Reveal>
        </div>

        <p className="mt-12 max-w-2xl text-xs leading-relaxed text-white/40">
          Nova works from the information you enter. It does not connect directly to your bank accounts. Bank-statement analysis works only on statements you choose to upload.
        </p>
      </div>
    </section>
  );
};

export default HowItWorks;
